import {
    ChatbubbleEllipsesOutline,
    PaperPlaneOutline,
    RefreshOutline,
    SparklesOutline,
  } from "@raresail/react-ionicons";
  import { IconButton } from "@mui/material";
  import { useEffect, useRef, useState } from "react";
  import { useNavigate } from "react-router-dom";
  import styled from "styled-components";
  import { Logo } from "../../../assets/svg/Svgs";
  import {
    primary5,
    primary10,
    PRIMARY_COLOR,
    PRIMARY_LIGHT,
  } from "../../../constants/colors";
  import { alpha } from "@mui/material";
  import { useTitle } from "../../../hooks/common/useTitle";
  import {
    useAdvisorChat,
    SUGGESTED_PROMPTS,
  } from "../../../hooks/advisor/useAdvisorChat";
  import { AdvisorAction, AdvisorMessage } from "../../../services/advisorService";
  
  export const AdvisorPage = () => {
    useTitle("Financial Advisor");
  
    const { messages, loading, send, clearChat } = useAdvisorChat();
    const [input, setInput] = useState("");
    const scrollRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
  
    // Auto-scroll on new messages
    useEffect(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, [messages, loading]);
  
    const handleSend = () => {
      if (!input.trim()) return;
      send(input);
      setInput("");
      inputRef.current?.focus();
    };
  
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };
  
    const handleAction = (action: AdvisorAction) => {
      if (action.type === "navigate" && action.target) {
        navigate(action.target);
      }
    };
  
    const handleSuggestion = (prompt: string) => {
      send(prompt);
    };
  
    const isFirstMessage = messages.length <= 1;
  
    return (
      <PageWrapper>
        {/* ── Header ── */}
        <Header>
          <div className="flexNullCenter gap10">
            <AvatarCircle>
              <img src={Logo} width={20} height={20} alt="advisor" />
            </AvatarCircle>
            <div>
              <div className="font14 semiBold textDark">Financial Advisor</div>
              <OnlineDot className="flexNullCenter gap5 font11">
                <span className="dot" />
                Online
              </OnlineDot>
            </div>
          </div>
          <IconButton size="small" onClick={clearChat} title="New conversation">
            <RefreshOutline fontSize={18} />
          </IconButton>
        </Header>
  
        {/* ── Messages ── */}
        <MessagesArea ref={scrollRef}>
          {/* Suggested prompts shown at start */}
          {isFirstMessage && (
            <SuggestionsGrid>
              <div className="font12 grey mb8 textCenter">
                Try asking about:
              </div>
              {SUGGESTED_PROMPTS.map((prompt, i) => (
                <SuggestionChip
                  key={i}
                  onClick={() => handleSuggestion(prompt)}
                  className="animate"
                >
                  <SparklesOutline fontSize={13} />
                  {prompt}
                </SuggestionChip>
              ))}
            </SuggestionsGrid>
          )}
  
          {/* Message list */}
          {messages.map((msg) => (
            <MessageBubble key={msg.id} msg={msg} onAction={handleAction} />
          ))}
  
          {/* Typing indicator */}
          {loading && (
            <BubbleRow>
              <AssistantAvatar>
                <img src={Logo} width={16} height={16} alt="" />
              </AssistantAvatar>
              <TypingBubble>
                <Dot style={{ animationDelay: "0ms" }} />
                <Dot style={{ animationDelay: "150ms" }} />
                <Dot style={{ animationDelay: "300ms" }} />
              </TypingBubble>
            </BubbleRow>
          )}
        </MessagesArea>
  
        {/* ── Input ── */}
        <InputArea>
          <InputWrapper>
            <StyledInput
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything about finances..."
              disabled={loading}
            />
            <SendButton
              onClick={handleSend}
              disabled={!input.trim() || loading}
              hasContent={!!input.trim()}
            >
              <PaperPlaneOutline fontSize={18} />
            </SendButton>
          </InputWrapper>
          <Disclaimer>
            AI-powered guidance. Not financial advice. Always do your own research.
          </Disclaimer>
        </InputArea>
      </PageWrapper>
    );
  };
  
  // ─── Message Bubble ─────────────────────────────────────────────
  const MessageBubble = ({
    msg,
    onAction,
  }: {
    msg: AdvisorMessage;
    onAction: (a: AdvisorAction) => void;
  }) => {
    const isUser = msg.role === "user";
    const action = msg.metadata?.action;
  
    return (
      <BubbleRow isUser={isUser}>
        {!isUser && (
          <AssistantAvatar>
            <img src={Logo} width={16} height={16} alt="" />
          </AssistantAvatar>
        )}
        <BubbleContent isUser={isUser}>
          <BubbleText isUser={isUser} dangerouslySetInnerHTML={{
            __html: formatContent(msg.content),
          }} />
  
          {/* Action button */}
          {action && action.type !== "none" && action.label && (
            <ActionButton
              onClick={() => onAction(action)}
              className="animate"
            >
              {action.label} →
            </ActionButton>
          )}
  
          <BubbleTime isUser={isUser}>
            {msg.timestamp.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </BubbleTime>
        </BubbleContent>
      </BubbleRow>
    );
  };
  
  // Simple markdown-like formatting
  function formatContent(text: string): string {
    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br/>");
  }
  
  // ─── Styles ─────────────────────────────────────────────────────
  
  const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 56px);
    max-width: 600px;
  
    @media (max-width: 960px) {
      height: calc(100vh - 112px);
    }
  `;
  
  const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #fff;
    border-bottom: 1px solid ${PRIMARY_LIGHT};
    flex-shrink: 0;
  `;
  
  const AvatarCircle = styled.div`
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: ${primary10};
    display: flex;
    align-items: center;
    justify-content: center;
  `;
  
  const OnlineDot = styled.div`
    color: #2e7d32;
  
    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #2e7d32;
    }
  `;
  
  const MessagesArea = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: ${primary5};
  
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: ${PRIMARY_LIGHT};
      border-radius: 4px;
    }
  `;
  
  const SuggestionsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 0 16px;
  `;
  
  const SuggestionChip = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: #fff;
    border: 1px solid ${PRIMARY_LIGHT};
    border-radius: 12px;
    font-size: 13px;
    color: ${PRIMARY_COLOR};
    cursor: pointer;
  
    &:hover {
      background: ${primary10};
      border-color: ${alpha(PRIMARY_COLOR, 0.2)};
    }
  `;
  
  const BubbleRow = styled.div<{ isUser?: boolean }>`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    justify-content: ${({ isUser }) => (isUser ? "flex-end" : "flex-start")};
  `;
  
  const AssistantAvatar = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 1px solid ${PRIMARY_LIGHT};
  `;
  
  const BubbleContent = styled.div<{ isUser?: boolean }>`
    max-width: 82%;
    display: flex;
    flex-direction: column;
    gap: 6px;
  `;
  
  const BubbleText = styled.div<{ isUser?: boolean }>`
    padding: 12px 16px;
    border-radius: ${({ isUser }) =>
      isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px"};
    background: ${({ isUser }) => (isUser ? PRIMARY_COLOR : "#fff")};
    color: ${({ isUser }) => (isUser ? "#fff" : "#1a1a2e")};
    font-size: 13px;
    line-height: 1.6;
    border: ${({ isUser }) =>
      isUser ? "none" : `1px solid ${PRIMARY_LIGHT}`};
  
    strong {
      font-weight: 600;
    }
  `;
  
  const BubbleTime = styled.div<{ isUser?: boolean }>`
    font-size: 10px;
    color: #999;
    text-align: ${({ isUser }) => (isUser ? "right" : "left")};
    padding: 0 4px;
  `;
  
  const ActionButton = styled.div`
    display: inline-flex;
    align-self: flex-start;
    padding: 6px 14px;
    background: ${primary10};
    color: ${PRIMARY_COLOR};
    font-size: 12px;
    font-weight: 600;
    border-radius: 20px;
    cursor: pointer;
  
    &:hover {
      background: ${alpha(PRIMARY_COLOR, 0.15)};
    }
  `;
  
  const TypingBubble = styled.div`
    display: flex;
    gap: 4px;
    padding: 14px 18px;
    background: #fff;
    border-radius: 18px 18px 18px 4px;
    border: 1px solid ${PRIMARY_LIGHT};
  `;
  
  const Dot = styled.div`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${alpha(PRIMARY_COLOR, 0.4)};
    animation: bounce 1s infinite;
  
    @keyframes bounce {
      0%,
      60%,
      100% {
        transform: translateY(0);
      }
      30% {
        transform: translateY(-6px);
      }
    }
  `;
  
  const InputArea = styled.div`
    padding: 12px 16px;
    background: #fff;
    border-top: 1px solid ${PRIMARY_LIGHT};
    flex-shrink: 0;
  `;
  
  const InputWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: ${primary5};
    border-radius: 25px;
    padding: 4px 6px 4px 18px;
    border: 1px solid ${PRIMARY_LIGHT};
  
    &:focus-within {
      border-color: ${alpha(PRIMARY_COLOR, 0.3)};
      box-shadow: 0 0 0 3px ${alpha(PRIMARY_COLOR, 0.06)};
    }
  `;
  
  const StyledInput = styled.input`
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: #1a1a2e;
    padding: 10px 0;
  
    &::placeholder {
      color: #999;
    }
  `;
  
  const SendButton = styled.button<{ hasContent: boolean }>`
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s;
    background: ${({ hasContent }) =>
      hasContent ? PRIMARY_COLOR : PRIMARY_LIGHT};
    color: ${({ hasContent }) => (hasContent ? "#fff" : "#999")};
  
    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  
    &:hover:not(:disabled) {
      transform: scale(1.05);
    }
  `;
  
  const Disclaimer = styled.div`
    font-size: 10px;
    color: #aaa;
    text-align: center;
    padding: 6px 0 0;
  `;